/**
 * Ryan O'Dowd
 * 2022-04-16
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ScrollView,
  View,
} from 'react-native';
import {
  WRINKLES_ROUTE,
  YEAR_IN_REVIEW_ROUTE,
  apiUpdateUser,
  blockUser,
  fetchPublicUser,
  fetchSevenDaysPeanuts,
  fetchStreaks,
  followUser,
  getRequestHeaders,
  reportUser,
  unblockUser,
  unfollowUser,
} from '@utils/store/actions';
import Button from '@components/common/Button';
import ContributionGraph from './ContributionGraph';
import Dialog from '@components/common/Dialog';
import EmptyScreen from '@components/common/EmptyScreen';
import FollowButton from '@components/FollowButton';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import {
  HelperText,
} from 'react-native-paper';
import Icon from '@react-native-vector-icons/material-design-icons';
import IconButton from '@components/common/IconButton';
import Menu from '@components/common/Menu';
import PeanutsAndWrinklesGraphWrapper from './PeanutsAndWrinklesGraphWrapper';
import Photo from '@components/Photo';
// @TODO: import ScreenLoader from '@components/ScreenLoader';
import ScreenWrapper from '@containers/ScreenWrapper';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import {
  format as dateFnsFormat,
} from 'date-fns';
import fb from '@utils/firebase';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const MIN_USERNAME_LENGTH = 4;

const DAYS_TO_GRAPH = 8;

const Profile = () => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const locationState = hooks.useLocationState();
  const styles = hooks.useStyles(getStyles);

  hooks.useBackButton(() => {
    return false;
  });

  const usernameExistsTimeoutRef = useRef(undefined);

  const betaOn = hooks.useTypedSelector((state) => state.userSettings.beta_on);

  const [_firebaseAuthUser, setFirebaseAuthUser] = useState(null);

  const [_usernameExists, setUsernameExists] = useState(null);
  const [_isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const [_isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const [_isLoggedInUserDataLoading, setIsLoggedInUserDataLoading] = useState(true);
  const [_isOtherUserDataLoading, setIsOtherUserDataLoading] = useState(true);
  const [_isReportDialogOpen, setIsReportUserDialogOpen] = useState(false);
  const [_isReportReasonDialogOpen, setIsReportReasonDialogOpen] = useState(false);
  const [_isReportSuccessDialogOpen, setIsReportSuccessDialogOpen] = useState(false);
  const [_reportReasonText, setReportReasonText] = useState('');

  const publicUsersSearched = hooks.useTypedSelector((state) => state.publicUsersSearched);
  const otherUser = useMemo(() => {
    return publicUsersSearched.find((u) => {
      return (u.id === locationState.otherUserId);
    });
  }, [publicUsersSearched, locationState.otherUserId]);

  const isFollowInFlight = hooks.useTypedSelector((state: any) => state.loadingStates.followUser?.userId === otherUser?.id);

  const loggedInUser = hooks.useTypedSelector((state) => state.oakUser); // @TODO: this is sometimes undefined or null during hot reload...why?
  const [lastSevenDaysPeanutsLoggedInUser, lastSevenDaysPassedVersesLoggedInUser, lastSevenDaysPeanutsOtherUser, lastSevenDaysPassedVersesOtherUser] = hooks.useTypedSelector((state) => { // @TODO: abstract this with PeanutsAndWrinklesGraph
    const peanutsData = state.peanutsSevenDays[loggedInUser.id]?.peanuts?.slice(-DAYS_TO_GRAPH) ?? [];
    const peanutsDataOther = state.peanutsSevenDays[otherUser?.id]?.peanuts?.slice(-DAYS_TO_GRAPH) ?? [];
    const passedVersesData = state.peanutsSevenDays[loggedInUser.id]?.passedVerses?.slice(-DAYS_TO_GRAPH) ?? [];
    const passedVersesDataOther = state.peanutsSevenDays[otherUser?.id]?.passedVerses?.slice(-DAYS_TO_GRAPH) ?? [];

    if (!peanutsData.length || (otherUser && !peanutsDataOther.length)) {
      return [[], [], [], []];
    }

    if (_isLoggedInUserDataLoading) {
      setIsLoggedInUserDataLoading(false);
    }
    if (_isOtherUserDataLoading) {
      setIsOtherUserDataLoading(false);
    }

    return [peanutsData, passedVersesData, peanutsDataOther, passedVersesDataOther];
  }, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue)); // @TODO: rename...more than 7 now

  const [__completedWrinkleGroups, inProgressWrinkleGroups] = hooks.useGetWrinkles(null, false, false, 'chronological', false, false, true);

  const numLifetimeWrinklesLoggedInUser = loggedInUser.lifetime_wrinkles;
  const numLifetimeWrinklesOtherUser = otherUser?.lifetime_wrinkles;

  const numPeanutsToday = lastSevenDaysPeanutsOtherUser[-1]?.peanuts || 0;
  const streaks = hooks.useTypedSelector((state) => state.streaks);

  const [_displayNameText, setDisplayNameText] = useState(loggedInUser.display_name);
  const [_usernameText, setUsernameText] = useState(loggedInUser.username);

  useEffect(() => {
    const getFirebaseUser = async () => {
      const u = await fb.auth.currentUser;
      setFirebaseAuthUser(u);
    };
    getFirebaseUser();
  }, []);

  useEffect(() => {
    dispatch(fetchStreaks()); // @TODO: also set local notification that is canceled (or pushed back another 24 hours) on lesson complete
    dispatch(fetchSevenDaysPeanuts(loggedInUser.id));
    if (locationState.otherUserId) {
      dispatch(fetchPublicUser(locationState.otherUserId));
      dispatch(fetchSevenDaysPeanuts(locationState.otherUserId));
    }
  }, [dispatch, loggedInUser, locationState.otherUserId]);

  const _getProviderInfo = () => {
    const providerIds = _firebaseAuthUser?.providerData?.map((p) => p.providerId) || []; // @TODO: should never be empty
    let iconName: any = 'account-question';
    if (providerIds.includes('google.com')) {
      iconName = 'google';
    } else if (providerIds.includes('apple.com')) {
      iconName = 'apple';
    } else if (providerIds.includes('password')) {
      iconName = 'email';
    }

    return (
      <View style={styles.providerInfoWrapper}>
        {loggedInUser.auth_provider_info !== 'anonymous' &&
          <>
            <Icon style={styles.providerIcon} name={iconName} />
            <Text style={styles.userInfoText}>{loggedInUser.auth_provider_info}</Text>
          </>
        }{/* @TODO: else, show option to link account with other provider */}
      </View>
    );
  };

  const isLoggedInUserFollowingOtherUser = hooks.useTypedSelector((state) => otherUser && state.usersFollowing.some((u) => u.id === otherUser.id));
  const hasLoggedInUserBlockedOtherUser = hooks.useTypedSelector((state) => otherUser && state.blockedUserIds.some((u) => u.id === otherUser.id));

  const _getYears = () => { // chatgpt
    const startYear = new Date(loggedInUser.created_at).getFullYear();
    const now = new Date();
    const currentYear = now.getFullYear();
    const includeCurrent = betaOn || now.getMonth() === 11; // 11 is december

    const endYear = includeCurrent ? currentYear : currentYear - 1;

    const years = [];
    for (let y = startYear; y <= endYear; y++) {
      years.push(y);
    }

    return years;
  };

  const yearInReviewYears = _getYears();
  const hasYearsInReview = betaOn && !otherUser && yearInReviewYears.length && loggedInUser.lifetime_peanuts > 0;

  const menuItem = (
    <Menu
      statusBarHeight={styles.safeAreaInsets.top}// @TODO: abstract menu to oak component and abstract this prop away
      anchor={
        <Header.Icon
          icon='dots-vertical'
          onPress={() => setIsOverflowMenuOpen(true)}
        />
      }
      visible={_isOverflowMenuOpen}
      onDismiss={() => setIsOverflowMenuOpen(false)}
    >
      {hasYearsInReview
        ? (
          yearInReviewYears.map((year) => {
            return (
              <Menu.Item
                key={year}
                title={`${year} Year in Review`}
                onPress={() => {
                  setIsOverflowMenuOpen(false);
                  navigation.navigate(YEAR_IN_REVIEW_ROUTE, {
                    year,
                  });
                }}
              />
            );
          })
        )
        : (
          <React.Fragment>
            <Menu.Item
              title={i18n('reportUser')}
              titleStyle={styles.reportUserText}
              onPress={() => {
                setIsReportUserDialogOpen(true);
                setIsOverflowMenuOpen(false);
              }}
            />
            <Menu.Item
              title={hasLoggedInUserBlockedOtherUser ? i18n('unblockUser') : i18n('blockUser')}
              onPress={() => {
                setIsOverflowMenuOpen(false);
                if (hasLoggedInUserBlockedOtherUser) {
                  dispatch(unblockUser(otherUser.id));
                } else {
                  dispatch(blockUser(otherUser.id));
                }
              }}
            />
            {!hasLoggedInUserBlockedOtherUser &&
              <Menu.Item
                title={isLoggedInUserFollowingOtherUser ? i18n('unfollowUser') : i18n('followUser')}
                disabled={isFollowInFlight}
                onPress={() => {
                  setIsOverflowMenuOpen(false);
                  if (isLoggedInUserFollowingOtherUser) {
                    dispatch(unfollowUser(otherUser.id));
                  } else {
                    dispatch(followUser(otherUser.id));
                  }
                }}
              />
            }
          </React.Fragment>
        )
      }
    </Menu>
  );

  const backIcon = <Header.BackIcon />;
  const rightItems = (otherUser || hasYearsInReview) ? [menuItem] : [];

  if (locationState.otherUserId && !otherUser) {
    // user is loading
    return (
      <ScreenWrapper>
        <Header
          title=''
          leftItems={[backIcon]}
        />
        <FullScreenScrollView>
          <EmptyScreen.Wrapper extraStyle={styles.emptyScreenWrapper}>{/* @TODO: make this match loading for wrapper on web so there's no jumping */}
            <EmptyScreen.Text>
              {i18n('loading')}...
            </EmptyScreen.Text>
          </EmptyScreen.Wrapper>
        </FullScreenScrollView>
      </ScreenWrapper>
    );
  }
  return (
    <ScreenWrapper>
      <Header
        title=''
        leftItems={[backIcon]}
        rightItems={rightItems}
      />
      <FullScreenScrollView>
        <View style={styles.innerContainer}>
          <ScrollView// @TODO: don't love that this is nested...
            contentContainerStyle={styles.list}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.profileDetails}>
              <View style={styles.userInfoWrapper}>
                <View style={styles.profilePicture}>
                  <Photo
                    style={styles.profilePicture}
                    displayName={!otherUser ? loggedInUser.display_name : otherUser.display_name}
                    username={!otherUser ? loggedInUser.username : otherUser.username}
                    readOnly={!!otherUser}
                    lastActive={otherUser ? otherUser.last_active : loggedInUser.last_active}
                    badgeSize={22}
                  />
                </View>
                <View style={styles.userInfoTextWrapper}>
                  <View style={styles.nameWrapper}>
                    <Text style={[styles.userInfoText, styles.displayName]}>{!otherUser ? loggedInUser.display_name : otherUser.display_name}</Text>
                    {!otherUser && // @TODO: should this be in the header instead?
                      <IconButton
                        icon='pencil'
                        style={styles.pencilIcon}
                        iconColor={styles.colors.white}
                        size={22}
                        onPress={() => setIsEditDialogOpen(true)}
                      />
                    }
                  </View>
                  <Text style={[styles.userInfoText, styles.username]}>{!otherUser ? loggedInUser.username : otherUser.username}</Text>
                </View>
              </View>
              <View style={styles.lastRow}>
                <View style={styles.userMetadata}>
                  {!otherUser && _getProviderInfo()}
                  <Text style={styles.userInfoText}>{i18n('wrinklingSince', dateFnsFormat(new Date(!otherUser ? loggedInUser.created_at : otherUser.created_at), 'MMMM yyyy', {locale: i18n.dateFnsLocale}))}</Text>
                </View>
                {otherUser && !isLoggedInUserFollowingOtherUser && !hasLoggedInUserBlockedOtherUser && // @TODO: and isn't alreaady following or blocked
                  <View style={styles.followButtonWrapper}>
                    <FollowButton userIdToFollow={otherUser.id} />
                  </View>
                }
              </View>
            </View>
            {/* @TODO:
            {_firebaseAuthUser.isAnonymous &&
              <>
                <Text>TODO: link to "link with auth provider" page</Text>
                <Text>TODO: explanation text about why that's useful</Text>
              </>
            }
            */}
            {/* @TODO:
            <Text>TODO: generate qr code to others to scan to send friend request (put in friends component too)</Text>
            <Text>TODO: scan qr code option (put in friends component too)</Text>
            */}

            <View style={styles.sectionWrapper}>
              <Text style={styles.sectionHeaderText}>{i18n('peanuts')}</Text>
              <View style={styles.streakWrapper}>{/* @TODO: make prettier */}
                <Text style={styles.streakText}>{i18n('lifetimePeanuts')}:</Text>
                <View style={styles.currentStreakWrapper}>
                  <Text style={styles.currentStreakText}>{loggedInUser.lifetime_peanuts}</Text>
                </View>
                {otherUser &&
                  <View style={[styles.currentStreakWrapper, styles.currentStreakWrapperLoggedInUser]}>
                    <Text style={styles.currentStreakText}>{otherUser.lifetime_peanuts}</Text>
                  </View>
                }
              </View>
              <View style={styles.streakWrapper}>{/* @TODO: make prettier */}
                <Text style={styles.streakText}>{i18n('weekPeanuts', DAYS_TO_GRAPH)}:</Text>{/* @TODO: if 0, gray background like streak */}
                <View style={styles.currentStreakWrapper}>
                  <Text style={styles.currentStreakText}>{lastSevenDaysPeanutsLoggedInUser.reduce((curr, p) => +p.peanuts + curr, 0)}</Text>
                </View>
                {otherUser &&
                  <View style={[styles.currentStreakWrapper, styles.currentStreakWrapperLoggedInUser]}>
                    <Text style={styles.currentStreakText}>{lastSevenDaysPeanutsOtherUser.reduce((curr, p) => +p.peanuts + curr, 0)}</Text>
                  </View>
                }
              </View>
              <View style={styles.streakWrapper}>{/* @TODO: make prettier */}
                <Text style={styles.streakText}>{i18n('versesLeveledUpInPastDays', DAYS_TO_GRAPH)}:</Text>{/* @TODO: if 0, gray background like streak */}
                <View style={styles.currentStreakWrapper}>
                  <Text style={styles.currentStreakText}>{lastSevenDaysPassedVersesLoggedInUser.reduce((curr, p) => +p.passed_verses + curr, 0)}</Text>
                </View>
                {otherUser &&
                  <View style={[styles.currentStreakWrapper, styles.currentStreakWrapperLoggedInUser]}>
                    <Text style={styles.currentStreakText}>{lastSevenDaysPassedVersesOtherUser.reduce((curr, p) => +p.passed_verses + curr, 0)}</Text>
                  </View>
                }
              </View>
              <View style={styles.peanutsGraphWrapper}>{/* @TODO: abstract this with streak */}
                {(_isLoggedInUserDataLoading || (otherUser && _isOtherUserDataLoading))
                  ? (
                    <EmptyScreen.Wrapper extraStyle={styles.emptyScreenWrapper}>
                      <EmptyScreen.Text>
                        {i18n('loadingGraph')}...
                      </EmptyScreen.Text>
                    </EmptyScreen.Wrapper>
                  ) : (
                    <PeanutsAndWrinklesGraphWrapper otherUser={otherUser} />
                  )
                }
              </View>
            </View>

            <View style={styles.sectionWrapper}>
              <Text style={styles.sectionHeaderText}>{i18n('streak')}</Text>
              <View style={styles.streakWrapper}>
                <Text style={styles.streakText}>{i18n('currentStreak')}:</Text>{/* @TODO: make prettier */}
                <View style={[styles.currentStreakWrapper, numPeanutsToday > 0 && styles.incompleteStreak, otherUser && styles.userToDisplayBackground]}>
                  <Text style={styles.currentStreakText}>{streaks.current_streak_num}</Text>
                </View>
                {otherUser &&
                  <View style={[styles.currentStreakWrapper, numPeanutsToday > 0 && styles.incompleteStreak, otherUser && styles.loggedInUserBackground]}>
                    <Text style={styles.currentStreakText}>{otherUser.streak_num ?? 0}</Text>
                  </View>
                }
              </View>
              {/* @TODO: better empty-screen handling */}
              {!otherUser &&
                <View style={styles.contributionGraphWrapper}>
                  <ContributionGraph
                    user={loggedInUser}
                    daysToDisplay={7 * 15}// @TODO: make this configurable
                    padRestOfWeek={true}
                  />
                </View>
              }
            </View>

            <View style={styles.sectionWrapper}>{/* @TODO: maybe this section could be abstracted with new Wrinkles container...or maybe this info in that container should be displayed differently */}
              <Text style={styles.sectionHeaderText}>{i18n('wrinkles')}</Text>
              {!otherUser &&
                <View style={styles.streakWrapper}>{/* @TODO: rename styles */}
                  <Text style={styles.streakText}>{i18n('inProgressWrinkles')}:</Text>
                  <View style={styles.currentStreakWrapper}>
                    <Text style={styles.currentStreakText}>{inProgressWrinkleGroups.reduce((curr, wrinkleSequence) => curr + wrinkleSequence.length, 0)}</Text>
                  </View>
                </View>
              }
              <View style={styles.streakWrapper}>{/* @TODO: rename styles */}
                <Text style={styles.streakText}>{i18n('lifetimeWrinkles')}:</Text>
                <View style={styles.currentStreakWrapper}>
                  <Text style={styles.currentStreakText}>{numLifetimeWrinklesLoggedInUser}</Text>
                </View>
                {otherUser &&
                  <View style={[styles.currentStreakWrapper, styles.currentStreakWrapperLoggedInUser]}>
                    <Text style={styles.currentStreakText}>{numLifetimeWrinklesOtherUser}</Text>
                  </View>
                }
              </View>
              {!otherUser &&
                <View style={styles.wrinklesButtonWrapper}>
                  <Button
                    mode='contained'
                    theme={styles.colors.accent}
                    onPress={() => navigation.navigate(WRINKLES_ROUTE)}
                  >
                    {i18n('seeAllWrinkles')}
                  </Button>
                </View>
              }
            </View>
            {/* @TODO: show following/followers */}
          </ScrollView>
        </View>
      </FullScreenScrollView>
      {_isEditDialogOpen &&
        <Dialog
          style={styles.editDialog}
          visible={_isEditDialogOpen}
          onDismiss={() => setIsEditDialogOpen(false)}
        >
          <Dialog.Title>{i18n('editProfile')}</Dialog.Title>
          {/* @TODO: need a keyboardavoidingview here */}
          <Dialog.Content>
            <TextInput
              mode='outlined'
              textColor={styles.colors.dialogText}
              selectionColor={styles.colors.primaryDark}
              value={_displayNameText}
              onChangeText={(t) => setDisplayNameText(t)}
              label={i18n('displayName')}
              placeholder={i18n('displayName')}
            />
            <TextInput
              mode='outlined'
              textColor={styles.colors.dialogText}
              selectionColor={styles.colors.primaryDark}
              autoCapitalize='none'
              value={_usernameText}
              maxLength={32}
              onChangeText={(t) => {
                t = t?.toLowerCase().replace(/[^a-z0-9]+/gi, '') || '';
                setUsernameText(t);
                setUsernameExists(null);
                clearTimeout(usernameExistsTimeoutRef.current);
                if (t.length >= MIN_USERNAME_LENGTH && t !== loggedInUser.username) {
                  usernameExistsTimeoutRef.current = setTimeout(async () => {
                    fetch(`${globals.apiUrl}/usernames/${t}/exists`, {
                      headers: await getRequestHeaders(),
                    }).then((response) => { // @TODO: abstract this to utilities and add auth headers
                      if (response.status !== 200) {
                        return null;
                      }
                      return response.text();
                    }).then((usernameExists) => {
                      setUsernameExists(Boolean(+usernameExists));
                    });
                  }, 400);
                }
              }}
              label={i18n('username')}
              placeholder={i18n('username')}
            />
            {_usernameText.length < MIN_USERNAME_LENGTH
              ? (
                <HelperText type='error' visible={_usernameText.length < MIN_USERNAME_LENGTH}>
                  {i18n('usernameInvalid', MIN_USERNAME_LENGTH)}
                </HelperText>
              ) : (
                <HelperText type={_usernameExists ? 'error' : 'info'} visible={_usernameExists !== null}>
                  {_usernameExists ? i18n('usernameExists') : i18n('usernameValid')}
                </HelperText>
              )
            }
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              textColor={styles.colors.primaryExtraDark}
              onPress={() => setIsEditDialogOpen(false)}
            >
              {i18n('cancel')}
            </Button>
            <Button
              mode='contained'
              disabled={_usernameExists || _usernameText.length < MIN_USERNAME_LENGTH}
              onPress={() => {
                const updatedFields: any = {};
                if (loggedInUser.display_name !== _displayNameText) {
                  utilities.updateDisplayName(fb.auth.currentUser, _displayNameText);
                }
                if (loggedInUser.username !== _usernameText) {
                  updatedFields.username = _usernameText;
                }
                if (Object.keys(updatedFields).length) {
                  dispatch(apiUpdateUser(updatedFields));
                }
                setIsEditDialogOpen(false);
                setUsernameExists(null);
              }}
            >
              {i18n('save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
      {_isReportDialogOpen &&
        <Dialog
          visible={_isReportDialogOpen}
          onDismiss={() => setIsReportUserDialogOpen(false)}
        >
          <Dialog.Title>{i18n('reportUserTitle')}</Dialog.Title>
          <Dialog.Content style={styles.dialogText}>
            {i18n('reportUserBody')}
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              onPress={() => setIsReportUserDialogOpen(false)}
            >
              {i18n('reportUserDoNotReport')}
            </Button>
            <Button
              mode='contained'
              theme={styles.colors.alert}
              onPress={() => {
                setIsReportUserDialogOpen(false);
                setIsReportReasonDialogOpen(true);
              }}
            >
              {i18n('reportUserConfirm')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
      {_isReportReasonDialogOpen &&
        <Dialog
          visible={_isReportReasonDialogOpen}
          onDismiss={() => setIsReportReasonDialogOpen(false)}
        >
          <Dialog.Title>{i18n('reportReasonTitle')}</Dialog.Title>
          <Dialog.Content style={styles.dialogText}>
            {i18n('reportReasonBody')}
            <TextInput// @TODO: eventually, would be nice if this were an enum (profile picture, display name/username, etc. and other)
              textColor={styles.colors.dialogText}
              selectionColor={styles.colors.primaryDark}
              value={_reportReasonText}
              onChangeText={(t) => setReportReasonText(t)}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              onPress={() => setIsReportReasonDialogOpen(false)}
            >
              {i18n('cancel')}
            </Button>
            <Button
              mode='contained'
              onPress={() => {
                dispatch(reportUser(otherUser.id, _reportReasonText));
                setIsReportReasonDialogOpen(false);
                setIsReportSuccessDialogOpen(true);
              }}
            >
              {i18n('reportUserReasonSubmit')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
      {_isReportSuccessDialogOpen &&
        <Dialog
          visible={_isReportSuccessDialogOpen}
          onDismiss={() => setIsReportSuccessDialogOpen(false)}
        >
          <Dialog.Title>{i18n('reportUserSuccessTitle')}</Dialog.Title>
          <Dialog.Content style={styles.dialogText}>
            {i18n('reportUserSuccessBody')}
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              onPress={() => setIsReportSuccessDialogOpen(false)}
            >
              {i18n('reportUserCancel')}
            </Button>
            <Button
              mode='contained'
              onPress={() => {
                setIsReportSuccessDialogOpen(false);
                navigation.goBack();
              }}
            >
              {i18n('close')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
    </ScreenWrapper>
  );
};

export default Profile;
